import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import { FaTrophy } from 'react-icons/fa';
import styles from './ResultABJ.module.css';
import "./Result.css";

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  try {
    if (cardCode.length < 2) {
      return null;
    }
    const cleanCode = cardCode.replace(/\.(jpg|png)$/i, '');
    return `/assets/img/cards/${cleanCode}.png`;
  } catch (e) {
    return null;
  }
};

// Parse card string to extract joker card and separate Andar/Bahar cards
// In Andar Bahar: First card is joker, then cards alternate between Andar and Bahar
const parseABJCards = (cardString, rdesc = "") => {
  if (!cardString) return { jokerCard: null, andarCards: [], baharCards: [] };

  const allItems = cardString.split(',').map(c => c.trim()).filter(c => c && c !== '1');
  
  // First card is the joker card
  const jokerCardCode = allItems[0] || null;
  const jokerCard = jokerCardCode ? {
    code: jokerCardCode,
    image: getCardImage(jokerCardCode)
  } : null;

  // Remaining cards are distributed alternately: Andar (even index), Bahar (odd index)
  const remainingCards = allItems.slice(1);
  
  const andarCards = [];
  const baharCards = [];
  
  remainingCards.forEach((cardCode, index) => {
    const card = {
      code: cardCode,
      image: getCardImage(cardCode)
    };
    // Alternate: even index (0, 2, 4...) -> Andar, odd index (1, 3, 5...) -> Bahar
    if (index % 2 === 0) {
      andarCards.push(card);
    } else {
      baharCards.push(card);
    }
  });

  return { jokerCard, andarCards, baharCards };
};

// Card Slider Component - Hooper-like implementation for React
const CardSlider = ({ cards, startCard }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef(null);
  const itemsToShow = 3;
  const slideWidth = 35;
  const slideGap = 5;

  useEffect(() => {
    if (startCard && cards.length > 0) {
      const index = cards.findIndex(card => card.code === startCard);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [startCard, cards]);

  useEffect(() => {
    if (trackRef.current) {
      const slideWithGap = slideWidth + slideGap;
      const translateX = -currentIndex * slideWithGap;
      trackRef.current.style.transform = `translateX(${translateX}px)`;
    }
  }, [currentIndex]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const goToNext = () => {
    const maxIndex = Math.max(0, cards.length - itemsToShow);
    setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : maxIndex));
  };

  if (!cards || cards.length === 0) return null;

  const maxIndex = Math.max(0, cards.length - itemsToShow);
  const isPrevDisabled = currentIndex === 0;
  const isNextDisabled = currentIndex >= maxIndex;

  return (
    <div className={styles.cardSliderWrapper}>
      <div className={styles.hooperSlider}>
        <div className={styles.hooperList}>
          <div 
            ref={trackRef}
            className={styles.hooperTrack}
          >
            {cards.map((card, index) => (
              <div
                key={`${card.code}-${index}`}
                className={`${styles.hooperSlide} ${index === currentIndex ? styles.isActive : ''}`}
              >
                {card.image && (
                  <img 
                    src={card.image} 
                    alt={card.code}
                    className={styles.cardImage}
                    onError={(e) => { e.target.style.display = 'none'; }} 
                  />
                )}
              </div>
            ))}
          </div>
          <div className={styles.hooperNavigation}>
            <div
              className={`${styles.hooperPrev} ${isPrevDisabled ? styles.isDisabled : ''}`}
              onClick={goToPrev}
              disabled={isPrevDisabled}
              aria-label="Previous slide"
            >
              <svg className={styles.navIcon} viewBox="0 0 24 24" width="20px" height="20px">
                <path d="M0 0h24v24H0z" fill="none"></path>
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" fill="currentColor"></path>
              </svg>
            </div>
            <div
              className={`${styles.hooperNext} ${isNextDisabled ? styles.isDisabled : ''}`}
              onClick={goToNext}
              disabled={isNextDisabled}
              aria-label="Next slide"
            >
              <svg className={styles.navIcon} viewBox="0 0 24 24" width="20px" height="20px">
                <path d="M0 0h24v24H0z" fill="none"></path>
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultABJ = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('abj');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            setLastResults(response.data.data.res);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            setLastResults(response.data.res);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch detail results when navigating between results
  const fetchDetailForIndex = useCallback(async (index) => {
    if (index >= 0 && index < lastResults.length && lastResults[index]?.mid) {
      try {
        const response = await getDetailResults('abj', lastResults[index].mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            return response.data.data.t1;
          } else if (response?.data?.t1) {
            return response.data.t1;
          }
        }
      } catch (error) {
        console.error(`Error fetching detail for index ${index}:`, error);
      }
    }
    return null;
  }, [lastResults]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setCurrentResultIndex(index);
    setShowModal(true);
    setLoading(true);

    const mid = resultItem?.mid;
    
    // Always fetch detail result for the clicked item
    if (mid) {
      try {
        const response = await getDetailResults('abj', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
    setCurrentResultIndex(0);
  };

  const goToPrevResult = useCallback(async () => {
    if (currentResultIndex > 0) {
      const newIndex = currentResultIndex - 1;
      setCurrentResultIndex(newIndex);
      setSelectedResult(lastResults[newIndex]);
      setLoading(true);
      const detail = await fetchDetailForIndex(newIndex);
      if (detail) {
        setDetailResult(detail);
      }
      setLoading(false);
    }
  }, [currentResultIndex, lastResults, fetchDetailForIndex]);

  const goToNextResult = useCallback(async () => {
    if (currentResultIndex < lastResults.length - 1) {
      const newIndex = currentResultIndex + 1;
      setCurrentResultIndex(newIndex);
      setSelectedResult(lastResults[newIndex]);
      setLoading(true);
      const detail = await fetchDetailForIndex(newIndex);
      if (detail) {
        setDetailResult(detail);
      }
      setLoading(false);
    }
  }, [currentResultIndex, lastResults, fetchDetailForIndex]);

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    // Use detailResult if available
    const cardData = detailResult?.card ? parseABJCards(detailResult.card, detailResult.rdesc) : { jokerCard: null, andarCards: [], baharCards: [] };
    const winValue = detailResult?.win || selectedResult.win;
    const isAndarWinner = winValue === "1";
    const isBaharWinner = winValue === "2";

    // Parse rdesc to get more info: "Bahar#Heart#Even#10"
    const rdescParts = detailResult?.rdesc ? detailResult.rdesc.split('#') : [];
    const winnerName = rdescParts[0] || detailResult?.winnat || (isAndarWinner ? "Andar" : "Bahar");

    return {
      roundId: detailResult?.rid || selectedResult.mid || "N/A",
      matchTime: detailResult?.mtime || new Date().toLocaleString(),
      jokerCard: cardData.jokerCard,
      andarCards: cardData.andarCards,
      baharCards: cardData.baharCards,
      isAndarWinner,
      isBaharWinner,
      winner: winnerName,
      rdesc: detailResult?.rdesc || ""
    };
  }, [selectedResult, detailResult, showModal]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : (data && data.length > 0 
        ? data.map((res, idx) => ({ win: res, mid: null }))
        : []);

  return (
    <>
      {/* Last Results Section */}
      {allResults.length > 0 && (
        <div className={styles.lastResults}>
          {allResults.map((resultItem, index) => {
            const winValue = resultItem.win;
            const displayValue = winValue === "1" ? "A" : winValue === "2" ? "B" : winValue;

            return (
              <span
                key={`${resultItem.mid || index}-${index}`}
                className={`${styles.resultCircle} ${displayValue === "A" ? styles.andar : styles.bahar}`}
                onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
                style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
              >
                {displayValue}
              </span>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Andar Bahar 2 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row row5 ab20-result abj-result text-center align-items-center">
              {/* A/B Labels Column */}
              <div className="col-1">
                <div className="row row5">
                  <div className="col-12">
                    <h4 className="mb-0" style={{ lineHeight: '50px' }}>
                      <b>A</b>
                    </h4>
                  </div>
                </div>
                <div className="row row5">
                  <div className="col-12">
                    <h4 className="mb-0" style={{ lineHeight: '50px' }}>
                      <b>B</b>
                    </h4>
                  </div>
                </div>
              </div>

              {/* Joker Card Column */}
              <div className="col-2">
                {modalData.jokerCard && modalData.jokerCard.image && (
                  <img
                    src={modalData.jokerCard.image}
                    alt={modalData.jokerCard.code}
                    className="card-right"
                    style={{ width: '35px', height: '50px', objectFit: 'contain', display: 'block' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>

              {/* Cards Column */}
              <div className="col-8">
                {/* Andar Cards */}
                <div className="card-inner">
                  <div className="row row5">
                    <div className="col-3">
                      {modalData.andarCards.length > 0 && modalData.andarCards[0]?.image && (
                        <img
                          src={modalData.andarCards[0].image}
                          alt={modalData.andarCards[0].code}
                          className="mb-1"
                          style={{ width: '35px', height: '50px', objectFit: 'contain', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="col-8">
                      <div className="ab-rtlslider">
                        <CardSlider
                          cards={modalData.andarCards.slice(1)}
                          startCard={modalData.andarCards[1]?.code}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bahar Cards */}
                <div className="card-inner">
                  <div className="row row5">
                    <div className="col-3">
                      {modalData.baharCards.length > 0 && modalData.baharCards[0]?.image && (
                        <img
                          src={modalData.baharCards[0].image}
                          alt={modalData.baharCards[0].code}
                          className="mb-1"
                          style={{ width: '35px', height: '50px', objectFit: 'contain', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="col-8">
                      <div className="ab-rtlslider">
                        <CardSlider
                          cards={modalData.baharCards.slice(1)}
                          startCard={modalData.baharCards[1]?.code}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Winner Icon Column */}
              <div className="col-1">
                <div className="row row5">
                  <div className="col-12 align-items-center d-flex" style={{ height: '50px' }}>
                    {modalData.isAndarWinner && (
                      <div className="winner-icon">
                        <FaTrophy className="mr-2" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="row row5">
                  <div className="col-12 align-items-center d-flex" style={{ height: '50px' }}>
                    {modalData.isBaharWinner && (
                      <div className="winner-icon">
                        <FaTrophy className="mr-2" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultABJ;

